const VALID_STATUSES = [
  'manually-added',
  'auto-added',
  'manually-removed',
  'auto-removed',
  'error-adding',
];

class SessionDiscountCodeStorage {
  #storageKey;
  #channel;
  #store;

  constructor(storageKey = 'discount_codes') {
    this.#storageKey = storageKey;
    this.#channel = new BroadcastChannel(`session-${this.#storageKey}`);
    this.#store = this.#load();

    this.#channel.onmessage = (event) => {
      if (event.data?.type === 'update') {
        this.#store = event.data.payload;
        sessionStorage.setItem(this.#storageKey, JSON.stringify(this.#store));
      }

      if (event.data?.type === 'init-reply') {
        this.#store = event.data.payload;
        sessionStorage.setItem(this.#storageKey, JSON.stringify(this.#store));
      }

      if (event.data?.type === 'init-request') {
        this.#channel.postMessage({
          type: 'init-reply',
          payload: this.#store,
        });
      }
    };

    this.#channel.postMessage({ type: 'init-request' });
  }

  #load() {
    const raw = sessionStorage.getItem(this.#storageKey);
    return raw ? JSON.parse(raw) : {};
  }

  #save(data) {
    this.#store = data;
    sessionStorage.setItem(this.#storageKey, JSON.stringify(data));
    this.#channel.postMessage({ type: 'update', payload: data });
  }

  #validateStatus(status) {
    if (!VALID_STATUSES.includes(status)) {
      throw new Error(
        `Invalid status: "${status}". Must be one of: ${VALID_STATUSES.join(', ')}`,
      );
    }
  }

  register(codes) {
    const codeList = Array.isArray(codes) ? codes : [codes];
    const store = { ...this.#store };

    codeList.forEach(({ code, status, metadata = {} }) => {
      if (!code || !status) {
        throw new Error(`Each discount must have both "code" and "status".`);
      }
      this.#validateStatus(status);

      if (store[code]) {
        store[code].status = status;
        store[code].metadata = metadata;
      } else {
        store[code] = { code, status, metadata };
      }
    });

    this.#save(store);
  }

  updateStatus({ codes, status }) {
    this.#validateStatus(status);
    const codeList = Array.isArray(codes) ? codes : [codes];
    const store = { ...this.#store };

    codeList.forEach((code) => {
      if (store[code]) {
        store[code].status = status;
      }
    });

    this.#save(store);
  }

  get(codeOrCodes) {
    const codes = Array.isArray(codeOrCodes) ? codeOrCodes : [codeOrCodes];
    const store = this.#store;

    const results = codes
      .map((code) => store[code] || null)
      .filter((code) => code !== null);
    return Array.isArray(codeOrCodes) ? results : results[0];
  }

  getAll() {
    return Object.values(this.#store);
  }

  clearAll() {
    this.#store = {};
    sessionStorage.removeItem(this.#storageKey);
    this.#channel.postMessage({ type: 'clear' });
  }
}
